/**
 * @file Builds the table column configuration for the switchable organizations list.
 */

import React from 'react';
import { Space, Typography } from 'antd';
import { EnterOutlined } from '@ant-design/icons';

import { useAppContext } from '@components/AppContext';
import { type AppContext } from '@components/AppContext/types';

const LABEL_CURRENT = 'Current';
const LABEL_NAME = 'Name';

type UseColumnsConfigOptions = {
  onBeforeSwitch: () => Promise<void> | void;
  onAfterSwitch: () => Promise<void> | void;
  highlightedOrganizationId?: string;
};

type OrganizationRecord = {
  id: string;
};

type NameColumnProps = {
  key: string;
  title: string;
  render: (value: string, organization: OrganizationRecord) => React.ReactNode;
  isSearchable: boolean;
};

/**
 * Builds the name column that switches to an organization and highlights the current and matched rows.
 */
const useColumnsConfig = ({
  onBeforeSwitch,
  onAfterSwitch,
  highlightedOrganizationId,
}: UseColumnsConfigOptions): NameColumnProps[] => {
  const { identity, switchToOrganization } = useAppContext() as AppContext;

  const currentOrganizationId = identity?.organization?.id;

  /**
   * Checks whether the given organization id is the identity's current organization.
   */
  const isCurrentOrganization = (id: string): boolean => id === currentOrganizationId;

  const nameColumnProps: NameColumnProps = {
    key: 'name',
    title: LABEL_NAME,
    /**
     * Renders the name cell, showing the current organization's badge or a link that switches to another organization.
     */
    render: (value: string, { id }: OrganizationRecord) => (
      (() => {
        const shouldShowEnterIcon = Boolean(
          highlightedOrganizationId && id === highlightedOrganizationId,
        );
        
        const left = isCurrentOrganization(id)
          ? (
            <Space>
              <Typography.Text strong>
                {value}
              </Typography.Text>
              <Typography.Text type="secondary">
                ({LABEL_CURRENT})
              </Typography.Text>
            </Space>
          )
          : (
            <Typography.Link
              strong
              onClick={() => Promise.resolve(onBeforeSwitch())
                .then(() => switchToOrganization(id))
                .then(() => onAfterSwitch())}
            >
              {value}
            </Typography.Link>
          );

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {left}
            {
              shouldShowEnterIcon && (
                <Typography.Text type="secondary">
                  <kbd>
                    <EnterOutlined />
                  </kbd>
                </Typography.Text>                
              )
            }
          </div>
        );
      })()
    ),
    isSearchable: true,
  };

  return [
    { ...nameColumnProps },
  ];
};

export default useColumnsConfig;
